import { cloneLaneLines, DetectionZone, Device, Facility, FacilityLayoutConfiguration, LaneLine, laneLinesEqual, ScreenLine, Spot } from "@app/shared";
import { useCallback, useEffect, useMemo, useState } from "react";
import { MapShapeTypes } from "../../maps";
import { ShapeFilter } from "../ShapeFilter";

export type Shape = DetectionZone | ScreenLine | Device | Spot | LaneLine;

interface UseShapeManagerProps {
    facility: Facility;
    shapeFilter: ShapeFilter;
}

export interface ShapeManagerState {
    localShapes: Shape[];
    selectedShapes: MapShapeTypes[];
    deletedShapeIds: Set<string>;

    mapShapes: Shape[];
    configShapes: Shape[];
    allShapes: Shape[];

    addShape: (shape: Shape) => void;
    updateShape: (shape: Shape) => void;
    deleteShape: (shapeId: string) => void;
    removeLocalShape: (shapeId: string) => void;
    selectShapes: (shapes: MapShapeTypes[]) => void;
    clearSelection: () => void;
    resetToFacilityState: () => void;

    laneLineSnapshot: LaneLine[] | null;
    canResetLaneLines: boolean;
    applySuggestedLaneLines: (generated: LaneLine[], existing: LaneLine[], replaceExisting: boolean) => void;
    restoreLaneLineSnapshot: () => void;

    getLayoutConfiguration: () => FacilityLayoutConfiguration;
    hasUnsavedChanges: () => boolean;
    getShapeById: (id: string) => Shape | undefined;
    removeIncompleteShapes: () => void;
    removeUnstartedShapes: () => void;
    hasIncompleteShape: () => boolean;
}

export function useShapeManager({ facility, shapeFilter }: UseShapeManagerProps): ShapeManagerState {
    const [localShapes, setLocalShapes] = useState<Shape[]>([]);
    const [selectedShapes, setSelectedShapes] = useState<MapShapeTypes[]>([]);
    const [deletedShapeIds, setDeletedShapeIds] = useState<Set<string>>(new Set());
    const [laneLineSnapshot, setLaneLineSnapshot] = useState<LaneLine[] | null>(null);

    const mapShapes = useMemo(() => {
        return getFacilityShapesForMap(facility, shapeFilter);
    }, [facility, shapeFilter]);

    const configShapes = useMemo(() => {
        return getFacilityShapesForConfig(facility, shapeFilter);
    }, [facility, shapeFilter]);

    // combined shapes for map display (original + local changes)
    const allShapes = useMemo(() => {
        const localShapeIds = new Set(localShapes.map((s) => s.id));
        const unchangedMapShapes = mapShapes.filter((s) => s.id && !localShapeIds.has(s.id) && !deletedShapeIds.has(s.id));
        const validLocalShapes = localShapes.filter((s) => !s.id || !deletedShapeIds.has(s.id));
        return [...unchangedMapShapes, ...validLocalShapes];
    }, [mapShapes, localShapes, deletedShapeIds]);

    useEffect(() => {
        setLocalShapes([]);
        setSelectedShapes([]);
        setDeletedShapeIds(new Set());
        setLaneLineSnapshot(null);
    }, [facility.id, facility.detectionZones?.length, facility.screenLines?.length, facility.laneLines?.length]);

    const addShape = useCallback((shape: Shape) => {
        setLocalShapes((current) => {
            const filtered = current.filter((s) => {
                const hasIsComplete = typeof (s as any).isComplete === "function";
                if (hasIsComplete && s.isComplete()) return true;
                if (!hasIsComplete) return true;
                if (s.id && !/^\d+$/.test(s.id)) return true;

                const existingShape = s as any;
                if (existingShape.points && Array.isArray(existingShape.points) && existingShape.points.length > 0) {
                    return true;
                }

                return false;
            });

            const existingShapeWithSameId = filtered.find((s) => s.id === shape.id);
            if (existingShapeWithSameId) {
                return current;
            }

            const newShapes = [...filtered, shape];
            return newShapes;
        });
        setSelectedShapes([shape as MapShapeTypes]);
    }, []);

    const updateShape = useCallback((updatedShape: Shape) => {
        setLocalShapes((current) => {
            const index = current.findIndex((s) => s.id === updatedShape.id);
            if (index >= 0) {
                const updated = [...current];
                updated[index] = updatedShape;
                return updated;
            }
            // If not found in local shapes, add it (it's being modified from facility)
            return [...current, updatedShape];
        });
    }, []);

    const deleteShape = useCallback(
        (shapeId: string) => {
            // Remove from local shapes
            setLocalShapes((current) => current.filter((s) => s.id !== shapeId));

            // Track deletion if it has a valid ID (exists in facility)
            const facilityShape = [...mapShapes, ...configShapes].find((s) => s.id === shapeId);
            if (facilityShape?.id) {
                setDeletedShapeIds((current) => new Set([...current, shapeId]));
            }

            // Remove from selection
            setSelectedShapes((current) => current.filter((s) => s.id !== shapeId));
        },
        [mapShapes, configShapes]
    );

    const removeLocalShape = useCallback((shapeId: string) => {
        setLocalShapes((current) => current.filter((s) => s.id !== shapeId));
        setSelectedShapes((current) => current.filter((s) => s.id !== shapeId));
    }, []);

    const selectShapes = useCallback((shapes: MapShapeTypes[]) => {
        setSelectedShapes((current) => {
            // Only update if actually different
            if (current.length === shapes.length && current.every((s) => shapes.find((ns) => ns.id === s.id))) {
                return current;
            }
            return shapes;
        });
    }, []);

    const clearSelection = useCallback(() => {
        setSelectedShapes([]);
    }, []);

    const resetToFacilityState = useCallback(() => {
        setLocalShapes([]);
        setSelectedShapes([]);
        setDeletedShapeIds(new Set());
        setLaneLineSnapshot(null);
    }, []);

    const applySuggestedLaneLines = useCallback(
        (generated: LaneLine[], existing: LaneLine[], replaceExisting: boolean) => {
            const generatedIds = new Set(generated.map((s) => s.id));

            setLocalShapes((current) => {
                const base = replaceExisting ? current.filter((s) => s.$type !== LaneLine.$type) : current.filter((s) => !generatedIds.has(s.id));
                return [...base, ...generated];
            });

            setDeletedShapeIds((current) => {
                if (!replaceExisting) return current;
                const next = new Set(current);
                (facility.laneLines ?? []).forEach((l) => l.id && next.add(l.id));
                return next;
            });

            setSelectedShapes(generated as MapShapeTypes[]);

            const resulting = replaceExisting ? generated : [...existing, ...generated];
            setLaneLineSnapshot(cloneLaneLines(resulting));
        },
        [facility.laneLines]
    );

    const restoreLaneLineSnapshot = useCallback(() => {
        if (!laneLineSnapshot) return;
        const snapshotIds = new Set(laneLineSnapshot.map((l) => l.id));

        setLocalShapes((current) => {
            const nonLaneLines = current.filter((s) => s.$type !== LaneLine.$type);
            return [...nonLaneLines, ...cloneLaneLines(laneLineSnapshot)];
        });

        setDeletedShapeIds((current) => {
            const next = new Set(current);
            (facility.laneLines ?? []).forEach((l) => {
                if (!l.id) return;
                if (snapshotIds.has(l.id)) next.delete(l.id);
                else next.add(l.id);
            });
            return next;
        });

        setSelectedShapes([]);
    }, [laneLineSnapshot, facility.laneLines]);

    const removeIncompleteShapes = useCallback(() => {
        setLocalShapes((current) =>
            current.filter((s) => {
                const hasIsComplete = typeof (s as any).isComplete === "function";
                return !hasIsComplete || s.isComplete();
            })
        );
        setSelectedShapes((current) =>
            current.filter((s) => {
                const hasIsComplete = typeof (s as any).isComplete === "function";
                return !hasIsComplete || s.isComplete();
            })
        );
    }, []);

    const removeUnstartedShapes = useCallback(() => {
        setLocalShapes((current) =>
            current.filter((s) => {
                const shape = s as any;
                const hasIsComplete = typeof shape.isComplete === "function";
                if (!hasIsComplete) return true;
                if (s.isComplete()) return true;

                // keep shapes that have been started (have meaningful points)
                if (shape.points && Array.isArray(shape.points)) {
                    // for detection zones, need at least 3 unique points to be considered "started"
                    if (shape.$type === DetectionZone.$type) {
                        return shape.points.length >= 3;
                    }
                    // for lines, need at least 2 points to be considered "started"
                    if (shape.$type === ScreenLine.$type || shape.$type === LaneLine.$type) {
                        return shape.points.length >= 2;
                    }
                }

                // remove shapes with no meaningful content
                return false;
            })
        );
        setSelectedShapes((current) =>
            current.filter((s) => {
                const shape = s as any;
                const hasIsComplete = typeof shape.isComplete === "function";
                if (!hasIsComplete) return true;
                if (s.isComplete()) return true;

                if (shape.points && Array.isArray(shape.points)) {
                    if (shape.$type === DetectionZone.$type) {
                        return shape.points.length >= 3;
                    }
                    if (shape.$type === ScreenLine.$type || shape.$type === LaneLine.$type) {
                        return shape.points.length >= 2;
                    }
                }

                return false;
            })
        );
    }, []);

    const hasIncompleteShape = useCallback(() => {
        return localShapes.some((s) => {
            const hasIsComplete = typeof (s as any).isComplete === "function";
            return hasIsComplete && !s.isComplete();
        });
    }, [localShapes]);

    const getLayoutConfiguration = useCallback((): FacilityLayoutConfiguration => {
        const facilityShapes = getFacilityShapesForConfig(facility, ShapeFilter.showAll());
        const localShapeIds = new Set(localShapes.map((s) => s.id));

        // Saving mid-draw is normal, and a zone that has the corners the drawing hint asked for
        // (MINIMUM_ZONE_VERTICES) is a shape the user finished drawing - closedRing() below repeats
        // its first vertex so it saves as the polygon they drew. Only an outline with too few
        // corners to be a polygon at all is left out, the way cancelling a draw leaves it out.
        const needsMoreCorners = (s: Shape) => s.$type === DetectionZone.$type && !(s as DetectionZone).canFormPolygon();
        const unsaveableZoneIds = new Set(localShapes.filter(needsMoreCorners).map((s) => s.id));

        // Such an edit to an *existing* zone falls back to that zone's saved outline. Dropping it
        // outright would omit the id, and an omitted id is how this payload says "deleted".
        const unchangedFacilityShapes = facilityShapes.filter(
            (s) => s.id && (!localShapeIds.has(s.id) || unsaveableZoneIds.has(s.id)) && !deletedShapeIds.has(s.id)
        );

        const allShapes = [...unchangedFacilityShapes, ...localShapes.filter((s) => !needsMoreCorners(s))];

        const laneLines = allShapes.filter((s) => s.$type === LaneLine.$type) as LaneLine[];
        const detectionZones = (allShapes.filter((s) => s.$type === DetectionZone.$type) as DetectionZone[]).map((z) => z.closedRing());
        const screenLines = allShapes.filter((s) => s.$type === ScreenLine.$type) as ScreenLine[];

        const config = new FacilityLayoutConfiguration();
        config.screenLines = screenLines;
        config.detectionZones = detectionZones;
        config.devices = allShapes.filter((s) => s.$type === Device.$type) as Device[];
        config.laneLines = laneLines;
        config.referencePoint = (allShapes.find((s) => s.$type === Spot.$type) as Spot) ?? facility.originReferencePointLatLng;

        return config;
    }, [facility, localShapes, deletedShapeIds]);

    const hasUnsavedChanges = useCallback(() => {
        return localShapes.length > 0 || deletedShapeIds.size > 0;
    }, [localShapes.length, deletedShapeIds.size]);

    const getShapeById = useCallback(
        (id: string): Shape | undefined => {
            return allShapes.find((s) => s.id === id);
        },
        [allShapes]
    );

    const canResetLaneLines = useMemo(() => {
        if (!laneLineSnapshot) return false;
        const current = (getLayoutConfiguration().laneLines ?? []) as LaneLine[];
        return !laneLinesEqual(current, laneLineSnapshot);
    }, [laneLineSnapshot, getLayoutConfiguration]);

    return {
        localShapes,
        selectedShapes,
        deletedShapeIds,
        mapShapes,
        configShapes,
        allShapes,
        addShape,
        updateShape,
        deleteShape,
        removeLocalShape,
        selectShapes,
        clearSelection,
        resetToFacilityState,
        laneLineSnapshot,
        canResetLaneLines,
        applySuggestedLaneLines,
        restoreLaneLineSnapshot,
        getLayoutConfiguration,
        hasUnsavedChanges,
        getShapeById,
        removeIncompleteShapes,
        removeUnstartedShapes,
        hasIncompleteShape,
    };
}

function getFacilityShapesForMap(facility: Facility, shapeFilter?: ShapeFilter): Shape[] {
    const shapes: Shape[] = [];

    if (shapeFilter?.showDetectionZonesOnMap !== false) {
        shapes.push(...(facility.detectionZones ?? []));
    }

    if (shapeFilter?.showScreenLinesOnMap !== false) {
        shapes.push(...(facility.screenLines ?? []));
    }

    if (shapeFilter?.showDevicesOnMap !== false) {
        shapes.push(...(facility.devices ?? []));
    }

    if (shapeFilter?.showLaneLinesOnMap !== false) {
        shapes.push(...(facility.laneLines ?? []));
    }

    if (shapeFilter?.showReferencePointOnMap !== false && facility.originReferencePointLatLng) {
        facility.originReferencePointLatLng.name = "Reference Point";
        shapes.push(facility.originReferencePointLatLng);
    }
    return shapes;
}

function getFacilityShapesForConfig(facility: Facility, shapeFilter?: ShapeFilter): Shape[] {
    const shapes: Shape[] = [];

    if (shapeFilter?.showDetectionZonesInConfig !== false) {
        shapes.push(...(facility.detectionZones ?? []));
    }

    if (shapeFilter?.showScreenLinesInConfig !== false) {
        shapes.push(...(facility.screenLines ?? []));
    }

    if (shapeFilter?.showDevicesInConfig !== false) {
        shapes.push(...(facility.devices ?? []));
    }

    if (shapeFilter?.showLaneLinesInConfig !== false) {
        shapes.push(...(facility.laneLines ?? []));
    }

    if (shapeFilter?.showReferencePointInConfig !== false && facility.originReferencePointLatLng) {
        facility.originReferencePointLatLng.name = "Reference Point";
        shapes.push(facility.originReferencePointLatLng);
    }

    return shapes;
}
